import { FC } from 'react';
import { ActivityType } from '@/utils/activity-calendar/enums/ActivityCalendar.enum';
import { TYPE_META } from '@/utils/activity-calendar/constants';

interface IProps {
  periodLabel: string;
  prevDisabled: boolean;
  nextDisabled: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  types: Record<ActivityType, boolean>;
  counts: Record<ActivityType, number>;
  onToggleType: (type: ActivityType) => void;
}

const NavButton: FC<{ disabled: boolean; onClick: () => void; label: string; children: React.ReactNode }> = ({
  disabled,
  onClick,
  label,
  children,
}) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex h-[36px] w-[36px] items-center justify-center rounded-[9px] border bg-white"
    style={{
      borderColor: '#D6DED6',
      color: disabled ? '#9CA3AF' : '#1F2937',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'default' : 'pointer',
    }}
  >
    {children}
  </button>
);

export const Toolbar: FC<IProps> = ({
  periodLabel,
  prevDisabled,
  nextDisabled,
  onPrev,
  onNext,
  onToday,
  types,
  counts,
  onToggleType,
}) => (
  <div
    className="flex flex-wrap items-center justify-between gap-[16px] border-b bg-[#F7F8F5] px-[18px] py-[13px]"
    style={{ borderColor: '#E5E7EB' }}
  >
    <div className="flex items-center gap-[10px]">
      <NavButton disabled={prevDisabled} onClick={onPrev} label="Previous period">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </NavButton>
      <span className="min-w-[188px] text-center font-display text-[18px] font-extrabold text-[#1F2937]">
        {periodLabel}
      </span>
      <NavButton disabled={nextDisabled} onClick={onNext} label="Next period">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </NavButton>
      <button
        type="button"
        onClick={onToday}
        className="ml-[6px] h-[36px] cursor-pointer rounded-full border bg-transparent px-[16px] font-sans text-[13px] font-bold text-primary"
        style={{ borderColor: '#A9D0BE' }}
      >
        Today
      </button>
    </div>

    <div className="flex flex-wrap items-center gap-[8px]">
      {TYPE_META.map((t) => {
        const active = types[t.key];
        return (
          <button
            key={t.key}
            type="button"
            title={`${active ? 'Hide' : 'Show'} ${t.label}`}
            onClick={() => onToggleType(t.key)}
            className="inline-flex h-[32px] items-center gap-[7px] rounded-full border px-[12px] font-sans text-[12.5px] font-bold"
            style={{
              cursor: 'pointer',
              background: active ? t.bg : '#fff',
              color: active ? t.fg : '#6B7280',
              borderColor: active ? 'transparent' : '#E5E7EB',
              opacity: active ? 1 : 0.7,
            }}
          >
            <span className="block h-[9px] w-[9px] rounded-full" style={{ background: active ? t.color : '#9CA3AF' }} />
            <span>{t.label}</span>
            <span
              className="rounded-full px-[7px] py-[1px] text-[11px] font-extrabold"
              style={{
                background: active ? 'rgba(255,255,255,0.6)' : '#F2F2F2',
                color: active ? t.fg : '#6B7280',
              }}
            >
              {counts[t.key]}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);
